import _ from 'lodash';
import moment from 'moment';
import { useImperativeHandle } from 'react';
import type { ForwardedRef } from 'react';
import { handleAddEcgBookmarks } from '@/entities/bookmark';
import { handleRequestFileUpload } from '@/shared/api';
import {
  handleReviewHolterBeats,
  handleUpdateBeatsAndEvents,
  handleUpdateMorphology,
  defaultBeatsOptions,
  addIsSavingToChangedData,
} from '@/entities/beat';
import { handleReviewHolterEvents } from '@/entities/event';
import { handleUpdateHolterHrDailySummary } from '@/entities/heart-rate';
import { handleUpdateHolterProfile } from '@/entities/study';
import { ANNOTATION_TYPE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  AIRP_TAB,
  ID_BOOKMARK_TAB,
  KEY_RECOIL,
  TYPE_BEAT_ENUM,
  TYPE_EVENT_OTHER,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import {
  resetHistoryState,
  updateStatusAndMetadata,
  isValidHttpUrl,
  putFileToUrl,
} from '@/shared/lib';
import { buildGroupEventsPayload, buildReviewHolterEventsPayload } from '@/shared/lib/aiUtils';
import emitter from '@/shared/lib/eventEmitter';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  defaultEventOptions,
  selectActiveTab,
  selectBeatChanges,
  selectBulkSwapBeats,
  selectEventChanges,
  selectEventNews,
  selectEventOthers,
  selectResetSwapBeats,
  selectSwapBeatChanges,
  selectTemplateChanges,
  selectBulkBeatsChanges,
  selectGroupEventChanges,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import { logError } from '@/shared/lib/apiHelpers';
import {
  formatMutationBeatsInput,
  formatMutationBeatsSwapInput,
  formatMutationReviewHolterBeatsInput,
} from './reviewBeatsHelper';
import { dataChartHeatMap } from './rrInterval/heatMapChart/handler';
import type { SummaryFunctionHandle, SummaryFunctionProps } from './summaryFunction';

/**
 * Headless controller for the Beats tab that owns the whole save pipeline and the tab's reset. Binds every pending-change slice of the report store to keyed getters and setters, then exposes onClickSaveReport, isChangedData and resetData through the forwarded ref, where saving uploads captured strips, fans beat, event, bookmark, daily-summary, review and morphology mutations out in parallel, stamps requestIds into localStorage and clears the undo history once any call succeeded.
 *
 * @returns Nothing; every value is reached only through the forwarded ref.
 */
const useSummaryFunction = (
  { studyId, profileId, isNotEdit }: SummaryFunctionProps,
  ref: ForwardedRef<SummaryFunctionHandle>,
) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  /**
   * Reads the pending other-type event edits for the Beats tab. Applies the event-others selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of other-type event changes.
   */
  const getEventOthers = () => selectEventOthers(keyRecoil)(useReportStore.getState());
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Writes the pending other-type event edits for the Beats tab. Forwards the value or updater to the store's setEventOthers action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  /**
   * Reads the newly drafted ECG events for the Beats tab. Applies the event-news selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of newly drafted events.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the newly drafted ECG events for the Beats tab. Forwards the value or updater to the store's setEventNews action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the pending edits to existing ECG events for the Beats tab. Applies the event-changes selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of pending event changes.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending edits to existing ECG events for the Beats tab. Forwards the value or updater to the store's setEventChanges action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  /**
   * Reads the pending per-beat edits for the Beats tab. Applies the beat-changes selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of pending beat changes.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Writes the pending per-beat edits for the Beats tab. Forwards the value or updater to the store's setBeatChanges action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  /**
   * Reads the pending group-level event changes for the Beats tab. Applies the group-event-changes selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of group event changes.
   */
  const getGroupEventChanges = () => selectGroupEventChanges(keyRecoil)(useReportStore.getState());
  const setGroupEventChangesAction = useReportStore((s) => s.actions.setGroupEventChanges);
  /**
   * Writes the pending group-level event changes for the Beats tab. Forwards the value or updater to the store's setGroupEventChanges action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setGroupEventChanges = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
    setGroupEventChangesAction(keyRecoil, v);
  /**
   * Reads the daily heart-rate summaries edited and not yet saved. Pulls updatedDailySummaries straight off the report store's current snapshot.
   *
   * @returns The array of edited daily summaries.
   */
  const getUpdatedDailySummaries = () => useReportStore.getState().updatedDailySummaries;
  const setUpdatedDailySummaries = useReportStore((s) => s.actions.setUpdatedDailySummaries);
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Closes the Beats tab's modal control state. Delegates to the store's resetControlModal action bound to this tab's keyRecoil.
   *
   * @returns The result of the store's reset action.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const resetControlModalHrSummary = useReportStore((s) => s.actions.resetControlHrSummaryModal);
  /**
   * Reads the study's ECG bookmarks, including any added locally and not yet persisted. Pulls ecgBookmarks straight off the report store's current snapshot.
   *
   * @returns The array of ECG bookmarks.
   */
  const getEcgBookmarks = () => useReportStore.getState().ecgBookmarks;
  const setEcgBookmarks = useReportStore((s) => s.actions.setEcgBookmarks);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the event undo and redo option snapshot for the Beats tab. Forwards the value or updater to the store's setEventOptions action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  /**
   * Reads which event category button is currently active on the Rhythm Events tab. Selects the active tab value for KEY_RECOIL.RHYTHM_EVENT from the report store's current snapshot rather than subscribing to it.
   *
   * @returns The active button identifier for the Rhythm Events tab.
   */
  const getActiveButtonTabRhythmEvent = () =>
    selectActiveTab(KEY_RECOIL.RHYTHM_EVENT)(useReportStore.getState());
  const setPageIndexTabRhythmEventAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Moves the Rhythm Events tab's pagination to a given page. Forwards the value or updater to the store's setPageIndex action with KEY_RECOIL.RHYTHM_EVENT prefilled rather than this tab's own key.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setPageIndexTabRhythmEvent = (v: Parameters<typeof setPageIndexTabRhythmEventAction>[1]) =>
    setPageIndexTabRhythmEventAction(KEY_RECOIL.RHYTHM_EVENT, v);
  const setIsDisabledBtn = useReportStore((s) => s.actions.setIsDisabledBtn);
  /**
   * Reads the identifying metadata of the report being edited, such as its profile and study ids. Pulls reportInfo straight off the report store's current snapshot.
   *
   * @returns The report info object.
   */
  const getReportInfo = () => useReportStore.getState().reportInfo;
  /**
   * Reads the pending bulk beat edits for the Beats tab. Applies the bulk-beats-changes selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of pending bulk beat changes.
   */
  const getBulkBeatsChanges = () => selectBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setBulkBeatsChangesAction = useReportStore((s) => s.actions.setBulkBeatsChanges);
  /**
   * Writes the pending bulk beat edits for the Beats tab. Forwards the value or updater to the store's setBulkBeatsChanges action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setBulkBeatsChanges = (v: Parameters<typeof setBulkBeatsChangesAction>[1]) =>
    setBulkBeatsChangesAction(keyRecoil, v);
  /**
   * Reads the bulk beat edits queued for reset on the Beats tab. Applies the reset-bulk-beats-changes selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of bulk beat changes queued for reset.
   */
  const getResetBulkBeatsChanges = () =>
    selectResetBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Writes the bulk beat edits queued for reset on the Beats tab. Forwards the value or updater to the store's setResetBulkBeatsChanges action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the Beats tab's undo and redo option snapshot. Forwards the value or updater to the store's setBeatOptions action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setIsPushQueue = useReportStore((s) => s.actions.setIsPushQueue);

  /**
   * Reads the beats marked reviewed in bulk and not yet saved. Pulls bulkReviewedBeats straight off the report store's current snapshot.
   *
   * @returns The array of bulk-reviewed beats.
   */
  const getBulkReviewedBeats = () => useReportStore.getState().bulkReviewedBeats;
  const setBulkReviewedBeats = useReportStore((s) => s.actions.setBulkReviewedBeats);
  /**
   * Reads the bulk beat review marks queued for reset. Pulls resetBulkReviewedBeats straight off the report store's current snapshot.
   *
   * @returns The array of bulk review marks queued for reset.
   */
  const getResetBulkReviewedBeats = () => useReportStore.getState().resetBulkReviewedBeats;
  const setResetBulkReviewedBeats = useReportStore((s) => s.actions.setResetBulkReviewedBeats);
  /**
   * Reads the per-beat review marks changed and not yet saved. Pulls reviewedBeatsChanges straight off the report store's current snapshot.
   *
   * @returns The array of changed beat review marks.
   */
  const getReviewedBeatsChanges = () => useReportStore.getState().reviewedBeatsChanges;
  const setReviewedBeatsChanges = useReportStore((s) => s.actions.setReviewedBeatsChanges);

  /**
   * Reads the pending per-beat morphology template swaps for the Beats tab. Applies the swap-beat-changes selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of pending beat swap changes.
   */
  const getSwapBeatChanges = () => selectSwapBeatChanges(keyRecoil)(useReportStore.getState());
  const setSwapBeatChangesAction = useReportStore((s) => s.actions.setSwapBeatChanges);
  /**
   * Writes the pending per-beat morphology template swaps for the Beats tab. Forwards the value or updater to the store's setSwapBeatChanges action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setSwapBeatChanges = (v: Parameters<typeof setSwapBeatChangesAction>[1]) =>
    setSwapBeatChangesAction(keyRecoil, v);
  /**
   * Reads the pending bulk morphology swap entries for the Beats tab. Applies the bulk-swap-beats selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of bulk swap beat entries.
   */
  const getBulkSwapBeats = () => selectBulkSwapBeats(keyRecoil)(useReportStore.getState());
  const setBulkSwapBeatsAction = useReportStore((s) => s.actions.setBulkSwapBeats);
  /**
   * Writes the pending bulk morphology swap entries for the Beats tab. Forwards the value or updater to the store's setBulkSwapBeats action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setBulkSwapBeats = (v: Parameters<typeof setBulkSwapBeatsAction>[1]) =>
    setBulkSwapBeatsAction(keyRecoil, v);
  /**
   * Reads the morphology swaps queued for reset on the Beats tab. Applies the reset-swap-beats selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of swaps queued for reset.
   */
  const getResetSwapBeats = () => selectResetSwapBeats(keyRecoil)(useReportStore.getState());
  const setResetSwapBeatsAction = useReportStore((s) => s.actions.setResetSwapBeats);
  /**
   * Writes the morphology swaps queued for reset on the Beats tab. Forwards the value or updater to the store's setResetSwapBeats action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setResetSwapBeats = (v: Parameters<typeof setResetSwapBeatsAction>[1]) =>
    setResetSwapBeatsAction(keyRecoil, v);
  /**
   * Reads the pending morphology template edits for the Beats tab. Applies the template-changes selector keyed by this tab's keyRecoil against the report store's current snapshot.
   *
   * @returns The array of pending template changes.
   */
  const getTemplateChanges = () => selectTemplateChanges(keyRecoil)(useReportStore.getState());
  const setTemplateChangesAction = useReportStore((s) => s.actions.setTemplateChanges);
  /**
   * Writes the pending morphology template edits for the Beats tab. Forwards the value or updater to the store's setTemplateChanges action with this tab's keyRecoil prefilled.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns The result of the store action.
   */
  const setTemplateChanges = (v: Parameters<typeof setTemplateChangesAction>[1]) =>
    setTemplateChangesAction(keyRecoil, v);

  /**
   * Builds the beat portion of the save mutation input from the pending single and bulk beat edits. Returns early when both lists are empty, otherwise formats them together with the queued bulk resets and the heat-map chart data, and discards the result when the formatter reports that nothing actually changed.
   *
   * @param beatChanges - Pending edits to individual beats.
   * @param bulkBeatsChange - Pending bulk beat edits.
   * @returns A promise resolving to the beat mutation input, or undefined when there is nothing to update.
   */
  const getInputUpdateBeats = async (beatChanges: any[] = [], bulkBeatsChange: any[] = []) => {
    if (beatChanges.length === 0 && bulkBeatsChange.length === 0) {
      return undefined;
    }
    const result = formatMutationBeatsInput({
      beatChanges,
      bulkBeatsChange,
      resetBulkBeatsChanges: getResetBulkBeatsChanges(),
      dataChartHeatMap,
    });
    if (!result.isUpdated) {
      return undefined;
    }
    return result.input;
  };

  //* Khi capture thì sẽ assign svg vào event rồi lúc nhấn save mới check và upload hình lấy url s3 về gửi lên
  /**
   * Prepares changed, new and other ECG events for saving and uploads any freshly captured strip images first. Filters other-events to those updated or with strip changes, clones them with the changes and new events, collects linked event ids, requests presigned URLs for every non-URL SVG strip and PUTs them, rewrites each capture's stripImgs and its start and stop from the sample-mark annotation, then builds the group-event and reviewed or unreviewed payloads.
   *
   * @param eChanges - Existing events whose type, timing or strips were edited.
   * @param eNews - Newly drafted events awaiting their first save.
   * @param eOthers - Other-type events, kept only when updated, image-changed or strip-removed.
   * @param groupEventChanges - Group-level event changes feeding the group and review payloads.
   * @returns A promise resolving to an object carrying changedEvents, allLinkEventIds, updateGroupEvents, reviewedEvents and unReviewedEvents, or undefined when the upload fails.
   */
  const formatEventsAndUploadCapturedImage = async (
    eChanges: any[] = [],
    eNews: any[] = [],
    eOthers: any[] = [],
    groupEventChanges: any[] = [],
  ) => {
    try {
      const eventOthers = _.filter(
        eOthers,
        (x: any) => !!x.isUpdatedOther || !!x.isRemovedStrip || !!x.isUpdateImage,
      );
      const changedEvents: any[] = _.cloneDeep([...eChanges, ...eNews, ...eventOthers]);
      const allLinkEventIds: any[] = [];
      const tempEventStripUpdate: any[] = [];
      const tempArrImgUpload: any[] = [];
      changedEvents.forEach((x: any, index: any) => {
        if (x.linkEvents?.length) {
          x.linkEvents.forEach((linkEvent: any) => allLinkEventIds.push(linkEvent.idEvent));
        }
        //* if not valid url => is svg => have updated strip
        _.forEach(x.captures, (capture: any) => {
          if (capture.stripImgs?.length && !isValidHttpUrl(capture.stripImgs[0])) {
            tempEventStripUpdate.push({
              index,
              captureId: capture.captureId,
              imagesCount: capture.stripImgs.length,
            });
            tempArrImgUpload.push(...capture.stripImgs);
          }
        });
      });
      if (changedEvents.length && tempArrImgUpload.length) {
        const uploadFileUrls = await handleRequestFileUpload('svg', tempArrImgUpload.length);
        if (uploadFileUrls?.length) {
          const putFileToUrlPromises = _.map(uploadFileUrls, (uploadFileUrl, index: any) =>
            putFileToUrl(uploadFileUrl, tempArrImgUpload[index], false),
          );
          await Promise.all(putFileToUrlPromises);
          _.forEach(tempEventStripUpdate, (data: any) => {
            const foundUpdatedStripEvent = changedEvents[data.index];
            const findCapture = _.find(
              foundUpdatedStripEvent.captures,
              (obj: any) => obj.captureId === data.captureId,
            );
            const findDraw = _.find(
              findCapture,
              (obj: any) => obj.type === ANNOTATION_TYPE.SAMPLE_MARK,
            );
            if (findDraw) {
              findCapture.start = findDraw.startTime;
              findCapture.stop = findDraw.endTime;
            }
            for (let i = 0; i < data.imagesCount; i += 1) {
              if (!_.isArray(findCapture.stripImgs)) {
                findCapture.stripImgs = [uploadFileUrls.shift()];
              } else {
                findCapture.stripImgs[i] = uploadFileUrls.shift();
              }
            }
          });
        }
      }

      const updateGroupEvents = buildGroupEventsPayload(groupEventChanges);
      const { reviewedEvents, unReviewedEvents } =
        buildReviewHolterEventsPayload(groupEventChanges);
      return {
        changedEvents,
        allLinkEventIds,
        updateGroupEvents,
        reviewedEvents,
        unReviewedEvents,
      };
    } catch (error) {
      console.log('Failed to upload file:', error);
    }
  };

  /**
   * Builds the event portion of the save mutation input from the changed events and any group-event changes. Returns early when both are empty, turns other-type events into a strip payload driven by the sample-mark annotation and any existing originalId, collapses deleted or artifact events to isDeleted or isMarkedAsArtifact unless a linked event restores the old type, pushes per-capture strip entries that omit fields unchanged from the original, and flags the event collections as saving.
   *
   * @param changedEvents - The events to translate into update payload entries.
   * @param allLinkEventIds - Ids of linked events, used to keep a deleted linked event's original type.
   * @param updateGroupEvents - Prebuilt group-event payload, included only when non-empty.
   * @returns A promise resolving to an object carrying updateGroupEvents and updateEvents, or undefined when there is nothing to update.
   */
  const getInputUpdateEvents = async ({
    changedEvents = [],
    allLinkEventIds = [],
    updateGroupEvents,
  }: {
    changedEvents?: any[];
    allLinkEventIds?: any[];
    updateGroupEvents?: any;
  }) => {
    const hasGroupEventChanges = !_.isEmpty(updateGroupEvents);
    const hasChangedEvents = changedEvents.length > 0;
    if (!hasChangedEvents && !hasGroupEventChanges) {
      return undefined;
    }
    const updateEvents = _.map(changedEvents, (k: any) => {
      if (k.type === TYPE_EVENT_OTHER) {
        const strip = k.captures?.[0];
        const isUpdatedOldEvent = k.isUpdatedOther && k.idEvent;
        const sampleMarkDraw = _.find(k.draw, (x: any) => x.type === ANNOTATION_TYPE.SAMPLE_MARK);
        return {
          start: k.start,
          stop: k.stop,
          type: k.type,
          title: k.title,
          isIncludedToReport: isUpdatedOldEvent ? k.isIncludedToReport : !_.isEmpty(k.stripImgs),
          id: k.idEvent || undefined,
          isDeleted: k.isRemovedStrip || undefined,
          strips: strip?.originalId
            ? [
                {
                  comment: k.technicianComments ?? undefined,
                  action: k.isRemovedStrip ? 'delete' : k.action || 'update',
                  id: strip?.originalId || undefined,
                  channel: strip?.isUpdateOtherImage
                    ? (sampleMarkDraw.channelIndex || 0) + 1
                    : undefined,
                  draw: strip?.isUpdateOtherImage ? k.draw : undefined,
                  images: strip?.isUpdateOtherImage ? k.stripImgs : undefined,
                },
              ]
            : [
                {
                  start: k.stripStart ?? k.start,
                  stop: k.stripStop ?? k.stop,
                  images: k.stripImgs,
                  isSelected: !_.isEmpty(k.stripImgs),
                  comment: k.technicianComments ?? undefined,
                  draw: k.draw,
                  action: k.isRemovedStrip ? 'delete' : k.action || 'update',
                  channel: (sampleMarkDraw?.channelIndex || 0) + 1,
                  id: strip?.id || undefined,
                },
              ],
        };
      }
      const formatUpdateEvent: any = {
        id: k.idEvent,
        start: k.start ? moment(k.start).toISOString() : undefined,
        stop: k.stop ? moment(k.stop).toISOString() : undefined,
        isReviewed: k.isReviewed,
        strips: k.isUpdateImage ? [] : undefined,
      };
      if ([TYPE_BEAT_ENUM.DELETED, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT].includes(k.type)) {
        if (allLinkEventIds.includes(k.idEvent) && k.type === TYPE_BEAT_ENUM.DELETED) {
          formatUpdateEvent.type = k.oldType;
        } else {
          formatUpdateEvent[
            k.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ? 'isMarkedAsArtifact' : 'isDeleted'
          ] = true;
        }
      } else {
        formatUpdateEvent.type = k.type;
      }
      if (k.isUpdateImage) {
        _.forEach(
          _.filter(
            [...k.captures, ...(k.removedCaptures || [])],
            (x) => !!x.action || k.isRemovedStrip,
          ),
          (capture) => {
            const findDraw = _.find(
              capture.draw,
              (obj: any) => obj.type === ANNOTATION_TYPE.SAMPLE_MARK,
            );
            const { originalStrip } = capture || {};
            if (k.isRemovedStrip && capture.action === 'delete') {
              formatUpdateEvent.strips.push({
                action: 'delete',
                id: capture.originalId,
              });
            } else if (!_.isEmpty(originalStrip)) {
              formatUpdateEvent.strips.push({
                id: capture.originalId,
                action: capture.action,
                comment:
                  originalStrip.comment === capture.technicianComments
                    ? undefined
                    : capture.technicianComments,
                start: _.isEqual(capture.stripImgs, originalStrip.images)
                  ? undefined
                  : findDraw.startTime,
                stop: _.isEqual(capture.stripImgs, originalStrip.images)
                  ? undefined
                  : findDraw.endTime,
                images: _.isEqual(capture.stripImgs, originalStrip.images)
                  ? undefined
                  : capture.stripImgs,
                draw: _.isEqual(capture.stripImgs, originalStrip.images) ? undefined : capture.draw,
                channel: _.isEqual(capture.stripImgs, originalStrip.images)
                  ? undefined
                  : (findDraw?.channelIndex || 0) + 1,
              });
            } else {
              formatUpdateEvent.strips.push({
                start: findDraw.startTime,
                stop: findDraw.endTime,
                images: capture.stripImgs,
                isSelected: !_.isEmpty(capture.stripImgs),
                comment: capture.technicianComments ?? undefined,
                draw: capture.draw,
                action: k.isRemovedStrip ? 'delete' : capture.action,
                id: capture.originalId || undefined,
                channel: (findDraw?.channelIndex || 0) + 1,
              });
              _.assign(formatUpdateEvent, { isIncludedToReport: !_.isEmpty(capture.stripImgs) });
            }
          },
        );
      }
      return formatUpdateEvent;
    });
    const input = {
      updateGroupEvents: !_.isEmpty(updateGroupEvents) ? updateGroupEvents : undefined,
      updateEvents: _.filter(updateEvents, (x: any) => !x.willBeRemoved),
    };
    // Perform grouping action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventNewsState: addIsSavingToChangedData(getEventNews()),
      eventChangesState: addIsSavingToChangedData(getEventChanges()),
      eventOthersState: addIsSavingToChangedData(getEventOthers()),
    }));
    setEventOthers(addIsSavingToChangedData(getEventOthers()));
    setEventChanges(addIsSavingToChangedData(getEventChanges()));
    setEventNews(addIsSavingToChangedData(getEventNews()));
    return input;
  };

  /**
   * Sends the combined beat and event edits for the Beats tab in a single mutation. Returns early when every incoming collection is empty, otherwise builds the beat input and the event input, posts them with the study and profile ids under the BEATS tab and the request id, and swallows any error to the console.
   *
   * @param info - Carries beatChanges, bulkBeatsChanges, changedEvents, allLinkEventIds, updateGroupEvents and the requestId.
   * @returns A promise resolving to true once the mutation is sent, or false when there was nothing to update or it failed.
   */
  const updateBeatsAndEvents = async (info: any) => {
    const {
      beatChanges,
      bulkBeatsChanges,
      changedEvents,
      allLinkEventIds,
      updateGroupEvents,
      requestId,
    } = info || {};
    if (
      beatChanges.length === 0 &&
      bulkBeatsChanges.length === 0 &&
      changedEvents.length === 0 &&
      _.isEmpty(allLinkEventIds) &&
      _.isEmpty(updateGroupEvents)
    ) {
      return false;
    }
    const beatData = await getInputUpdateBeats(beatChanges, bulkBeatsChanges);
    const eventData = await getInputUpdateEvents({
      changedEvents,
      allLinkEventIds,
      updateGroupEvents,
    });
    try {
      const input = {
        studyId,
        profileId,
        tab: AIRP_TAB.BEATS,
        requestId,
        beatData,
        eventData,
      };
      await handleUpdateBeatsAndEvents(input);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  /**
   * Persists beat review marks for the holter study, covering bulk marks, their queued resets and individual changes. Formats them against the heat-map chart data, returns early when the formatter reports no review change, otherwise sends the review mutation with the study and profile ids and swallows any error to the console.
   *
   * @param requestId - Correlation id echoed back on the socket response for this save.
   * @returns A promise resolving to true once the review marks are sent, or false when nothing changed or the mutation failed.
   */
  const updateReviewHolterBeats = async (requestId: any) => {
    try {
      const reviewedData = formatMutationReviewHolterBeatsInput({
        bulkReviewedBeats: getBulkReviewedBeats(),
        resetBulkReviewedBeats: getResetBulkReviewedBeats(),
        reviewedBeatsChanges: getReviewedBeatsChanges(),
        dataChartHeatMap,
      });
      if (!reviewedData.isUpdatedReview) return false;
      const input = {
        requestId,
        studyId,
        profileId,
        ...reviewedData.input,
      };

      await handleReviewHolterBeats(input);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  /**
   * Persists reviewed and unreviewed markings for holter ECG events. Destructures the two lists and the request id, returns early when both are empty, otherwise sends them with the study and profile ids through the review mutation and swallows any error to the console.
   *
   * @param info - Carries reviewedEvents, unReviewedEvents and the requestId for this save.
   * @returns A promise resolving to true once the markings are sent, or false when both lists were empty or the mutation failed.
   */
  const updateReviewHolterEvents = async (info: any) => {
    const { reviewedEvents = [], unReviewedEvents = [], requestId } = info || {};
    if (_.isEmpty(reviewedEvents) && _.isEmpty(unReviewedEvents)) {
      return false;
    }
    try {
      const input = {
        studyId,
        profileId,
        requestId,
        reviewedEvents,
        unReviewedEvents,
      };
      await handleReviewHolterEvents(input);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  /**
   * Persists edited daily heart-rate summaries for the holter profile. Bails out on empty input, reshapes each row down to date, average, max and min heart rate with their times, userUpdated flags and head or tail beat anchors plus suggestHrsOnBeat, sends them through the holter daily-summary mutation, and on failure logs the error and raises an error toast.
   *
   * @param updateData - The edited daily summary rows to persist.
   * @returns A promise resolving to true once the summaries are saved, or false when there was nothing to send or the mutation failed.
   */
  const updateDailySummaries = async (updateData: any) => {
    try {
      if (_.isEmpty(updateData)) return false;
      const formatData = _.map(updateData, (item: any) => ({
        date: item.date,
        avgHr: item.avgHr,
        maxHr: {
          time: item.maxHr?.time,
          userUpdated: item.maxHr?.userUpdated,
          value: item.maxHr?.value,
          hrsOnBeat: {
            head: item.maxHr?.hrsOnBeat?.head,
            tail: item.maxHr?.hrsOnBeat?.tail,
          },
        },
        minHr: {
          time: item.minHr?.time,
          userUpdated: item.minHr?.userUpdated,
          value: item.minHr?.value,
          hrsOnBeat: {
            head: item.minHr?.hrsOnBeat?.head,
            tail: item.minHr?.hrsOnBeat?.tail,
          },
        },
        suggestHrsOnBeat: item.suggestHrsOnBeat,
      }));
      await handleUpdateHolterHrDailySummary({
        profileId,
        mctDailySummaries: formatData,
      });
      return true;
    } catch (error) {
      logError('Failed to save: ', error);
      toastrError(error.message, 'Error');
      return false;
    }
  };

  /**
   * Persists the ECG bookmarks that were added locally and still lack an id. Clones the list, removes the id-less entries and bails when there are none, sends their start, stop and notes to the add-bookmark mutation, merges the returned records back in sorted by center, resets the Rhythm Events pagination while the bookmark tab is open, and reports false because bookmarks get no socket response.
   *
   * @param ecgBookmarks - The current bookmark list, including unsaved additions.
   * @returns A promise resolving to false in every path, since bookmark saves need no socket acknowledgement.
   */
  const updateEcgBookmarks = async (ecgBookmarks: any[] = []) => {
    try {
      const clonedEcgBookmarks = _.cloneDeep(ecgBookmarks);
      const newEcgBookmarks = _.remove(clonedEcgBookmarks, (x: any) => !x.id);
      if (_.isEmpty(newEcgBookmarks)) return false;
      const addedEcgBookmarks = await handleAddEcgBookmarks({
        studyId,
        profileId,
        ecgBookmarks: _.map(newEcgBookmarks, (x: any) => ({
          start: x.start,
          stop: x.stop,
          notes: x.notes,
        })),
      });
      setEcgBookmarks(
        _.sortBy(clonedEcgBookmarks.concat(addedEcgBookmarks), [(x: any) => x.center]),
      );
      if ((getActiveButtonTabRhythmEvent() as any) === ID_BOOKMARK_TAB) {
        setPageIndexTabRhythmEvent({ index: 0 });
      }
      //* Ecg bookmark dont need socket response so return false
      return false;
    } catch (error) {
      logError('Failed to save: ', error);
      toastrError(error.message, 'Error');
      return false;
    }
  };

  /**
   * Persists pending beat morphology template swaps and their include-in-report flags for the study. Formats the swap changes, template changes, bulk swaps and queued resets against the heat-map chart data, skips the call when the formatter reports no change, otherwise sends the BEAT_HR morphology mutation and on failure logs and toasts the error.
   *
   * @param requestId - Correlation id echoed back on the socket response for this save.
   * @returns A promise resolving to true once the swaps are sent, or false when nothing changed or the mutation failed.
   */
  const updateMorphologyBeats = async (requestId: any) => {
    try {
      const result = formatMutationBeatsSwapInput({
        swapBeatChanges: getSwapBeatChanges(),
        templateChanges: getTemplateChanges(),
        bulkSwapBeats: getBulkSwapBeats(),
        resetSwapBeats: getResetSwapBeats(),
        dataChartHeatMap,
      });
      if (!result.isUpdated) {
        return false;
      }
      const input = {
        studyId,
        profileId,
        requestId,
        tab: 'BEAT_HR',
        morphologyBeatTemplateSwap: result.morphologyBeatTemplateSwap,
        includeMorphologyToReport: result.includeMorphologyToReport,
      };
      await handleUpdateMorphology(input);
      return true;
    } catch (error) {
      logError('Failed to save: ', error);
      toastrError(error.message, 'Error');
      return false;
    }
  };

  /**
   * Flags every pending beat-side change as in-flight so a second save click cannot resubmit it. Maps addIsSavingToChangedData over the beat changes, bulk changes, their resets, review marks, swaps and template changes, and mirrors the same flagged snapshots into beatOptions for undo and redo.
   *
   * @returns Nothing.
   */
  const addSavingBeatChanges = () => {
    setBeatChanges((prev: any) => addIsSavingToChangedData(prev));
    setBulkBeatsChanges((prev: any) => addIsSavingToChangedData(prev));
    setResetBulkBeatsChanges((prev: any) => addIsSavingToChangedData(prev));
    setReviewedBeatsChanges((prev: any) => addIsSavingToChangedData(prev));
    setBulkReviewedBeats((prev: any) => addIsSavingToChangedData(prev));
    setResetBulkReviewedBeats((prev: any) => addIsSavingToChangedData(prev));
    setSwapBeatChanges((prev: any) => addIsSavingToChangedData(prev));
    setBulkSwapBeats((prev: any) => addIsSavingToChangedData(prev));
    setResetSwapBeats((prev: any) => addIsSavingToChangedData(prev));
    setTemplateChanges((prev: any) => addIsSavingToChangedData(prev));

    setBeatOptions((prev: any) => ({
      ...prev,
      beatChangesState: addIsSavingToChangedData(getBeatChanges()),
      bulkBeatsChangesState: addIsSavingToChangedData(getBulkBeatsChanges()),
      resetBulkBeatsChangesState: addIsSavingToChangedData(getResetBulkBeatsChanges()),
      reviewedBeatsChangesState: addIsSavingToChangedData(getReviewedBeatsChanges()),
      bulkReviewedBeatsState: addIsSavingToChangedData(getBulkReviewedBeats()),
      resetBulkReviewedBeatsState: addIsSavingToChangedData(getResetBulkReviewedBeats()),
      swapBeatChangesState: addIsSavingToChangedData(getSwapBeatChanges()),
      bulkSwapBeatsState: addIsSavingToChangedData(getBulkSwapBeats()),
      resetSwapBeatsState: addIsSavingToChangedData(getResetSwapBeats()),
    }));
  };

  /**
   * Decides whether the Beats tab holds any unsaved edit across beats, events, bookmarks, daily summaries, reviews, swaps and templates. Reads each pending collection from the store and ORs their lengths together, first dropping entries already flagged isSaving when the check runs for a save click, and counting a bookmark without an id as unsaved.
   *
   * @param isClickSave - Set when the check runs for a save click, which excludes entries already being saved.
   * @returns True when at least one unsaved change is present.
   */
  const checkChangedData = (isClickSave = false) => {
    const beatChanges = getBeatChanges();
    const eventChanges = getEventChanges();
    const eventNews = getEventNews();
    const groupEventChanges = getGroupEventChanges();
    const updatedDailySummaries = getUpdatedDailySummaries();
    const eventOthers = getEventOthers();
    const ecgBookmarks = getEcgBookmarks();
    const bulkBeatsChanges = getBulkBeatsChanges();
    const bulkReviewedBeats = getBulkReviewedBeats();
    const reviewedBeatsChanges = getReviewedBeatsChanges();
    const isChangedReviewed = !!bulkReviewedBeats.length || !!reviewedBeatsChanges.length;
    const bulkSwapBeats = getBulkSwapBeats();
    const swapBeatChanges = getSwapBeatChanges();
    const isChangedSwap = !!bulkSwapBeats.length || !!swapBeatChanges.length;

    const hasChangedEcgBookmarks = ecgBookmarks.find((x) => !x.id);
    const filterNotSavingNews = isClickSave
      ? _.filter(eventNews, (x: any) => !x.isSaving)
      : eventNews;
    const filterNotSavingChanges = isClickSave
      ? _.filter(eventChanges, (x: any) => !x.isSaving)
      : eventChanges;
    const filterNotSavingOthers = isClickSave
      ? _.filter(eventOthers, (x: any) => !x.isSaving)
      : eventOthers;
    const filterNotSavingBeats = isClickSave
      ? _.filter(beatChanges, (x: any) => !x.isSaving)
      : beatChanges;
    const templateChanges = getTemplateChanges();

    return (
      !!filterNotSavingBeats.length ||
      !!filterNotSavingChanges.length ||
      !!filterNotSavingNews.length ||
      !!groupEventChanges.length ||
      !!filterNotSavingOthers.length ||
      !!updatedDailySummaries.length ||
      !!hasChangedEcgBookmarks ||
      !!bulkBeatsChanges.length ||
      isChangedReviewed ||
      isChangedSwap ||
      !!templateChanges.length
    );
  };

  useImperativeHandle(ref, () => ({
    onClickSaveReport: async () => {
      if (!checkChangedData(true) && !isNotEdit) {
        return;
      }
      setIsPushQueue(true);
      emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: true, isSaving: true });
      const requestId = `requests:${profileId}:${Date.now()}`;

      const {
        changedEvents,
        allLinkEventIds,
        updateGroupEvents,
        reviewedEvents,
        unReviewedEvents,
      } = await formatEventsAndUploadCapturedImage(
        getEventChanges(),
        getEventNews(),
        getEventOthers(),
        getGroupEventChanges(),
      );
      const info: any = getReportInfo();
      const lastModified = moment();
      const keyStorage = `requestId-${info.profileId}-${info.studyFid}`;
      let requestData: any = localStorage.getItem(keyStorage);
      if (_.isEmpty(requestData) || !Array.isArray(requestData)) {
        requestData = [
          {
            requestId,
            type: 'BEAT_HR',
          },
        ];
      } else {
        requestData.push({
          requestId,
          type: 'BEAT_HR',
        });
      }
      localStorage.setItem(keyStorage, JSON.stringify(requestData));
      // pre-existing: localStorage.setItem's 2nd arg must be a string; these pass a boolean.
      localStorage.setItem(`ai_process-${info.profileId}`, false as any);
      localStorage.setItem(`ai_saving-${info.profileId}`, true as any);
      const updateHolterProfileInput = {
        id: info.profileId,
        lastModified,
      };

      const isUpdateBeatsAndEvents = await updateBeatsAndEvents({
        beatChanges: getBeatChanges(),
        bulkBeatsChanges: getBulkBeatsChanges(),
        changedEvents,
        allLinkEventIds,
        updateGroupEvents,
        requestId,
      });
      const hasReviewAction = !_.isEmpty(reviewedEvents) || !_.isEmpty(unReviewedEvents);
      // requestId should different because BE check 2 cmd
      const isReviewHolterEvents = hasReviewAction
        ? await updateReviewHolterEvents({
            reviewedEvents,
            unReviewedEvents,
            requestId: `requests:${profileId}:${Date.now()}`,
          })
        : false;

      const promise = [
        updateEcgBookmarks(getEcgBookmarks()),
        updateReviewHolterBeats(requestId),
        // updateDailySummaries only declares (updateData) - this call site's 2nd arg (requestId) is unused, pre-existing.
        (updateDailySummaries as any)(getUpdatedDailySummaries(), requestId),
        updateMorphologyBeats(requestId),
        handleUpdateHolterProfile(updateHolterProfileInput),
        updateStatusAndMetadata({ lastModified } as any),
      ];
      addSavingBeatChanges();
      // update last modified check download data
      const [
        isSuccessEcgBookmark,
        isSuccessRrReview,
        isSuccessDailySummaries,
        isSuccessMorphologyBeats,
      ] = await Promise.all(promise);
      // *: If all update data fail
      if (
        !(
          isSuccessEcgBookmark ||
          isSuccessRrReview ||
          isReviewHolterEvents ||
          isSuccessDailySummaries ||
          isUpdateBeatsAndEvents ||
          isSuccessMorphologyBeats
        )
      ) {
        emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false });
        emitter.emit(EMITTER_CONSTANTS.RE_RENDER_ECG_VIEWER);
      } else {
        resetControlModal();
        resetControlModalHrSummary();
      }
      setEventOptions(defaultEventOptions);
      resetHistoryState();
      setTimeout(() => {
        setIsDisabledBtn(false);
      }, 1000);
    },
    isChangedData: () => checkChangedData() && !isNotEdit,
    resetData: () => {
      setEventChanges([]);
      setEventNews([]);
      setGroupEventChanges([]);
      setEventOthers([]);
      setBeatOptions(defaultBeatsOptions);
      setUpdatedDailySummaries([]);
      setEcgBookmarks((prev) => prev.filter((x) => x.id));
      setEventOptions(defaultEventOptions);

      setBeatChanges([]);
      setBulkBeatsChanges([]);
      setResetBulkBeatsChanges([]);

      setReviewedBeatsChanges([]);
      setBulkReviewedBeats([]);
      setResetBulkReviewedBeats([]);

      setSwapBeatChanges([]);
      setBulkSwapBeats([]);
      setResetSwapBeats([]);
      setTemplateChanges([]);
      //* Redraw heatmap chart when reset review
      setTimeout(() => {
        emitter.emit(EMITTER_CONSTANTS.UPDATE_HEATMAP_CHART_DATA);
      }, 0);
    },
  }));
};

export default useSummaryFunction;
